import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCycle, useCompensations, useReportingLines, deleteCompensation, updateCycle, deleteAllReportingLines, stampSupervisorChains } from '../../hooks/useFirestore';
import { CYCLE_STATUSES, CATEGORY_OPTIONS } from '../../constants';
import { formatCurrency, pctChange } from '../../utils/formatters';
import { cn } from '../../utils/cn';
import { rollupHealth } from '../../utils/hierarchy';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Input from '../../components/ui/Input';
import Notice from '../../components/ui/Notice';
import Select from '../../components/ui/Select';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import AnimatedList from '../../components/ui/AnimatedList';
import { Avatar } from '../../components/ui/StatCard';
import { SkeletonRows } from '../../components/ui/Skeleton';
import { useToast } from '../../contexts/ToastContext';
import CompensationForm from './CompensationForm';
import CsvImportModal from './CsvImportModal';
import ReportingImportModal from './ReportingImportModal';
import OrgTreeView from './OrgTreeView';

export default function CycleDetail() {
  const { cycleId } = useParams();
  const nav = useNavigate();
  const toast = useToast();
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);
  const { cycle, loading: cycleLoading } = useCycle(cycleId);
  const { comps, loading: compsLoading } = useCompensations(cycleId, refreshKey);
  const { lines: reportingLines, loading: rlLoading } = useReportingLines(cycleId, refreshKey);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [editComp, setEditComp] = useState(null); // null = closed, 'new' = create, object = edit
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [cycleName, setCycleName] = useState('');
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [reportingModalOpen, setReportingModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('employees');
  const [clearRlConfirm, setClearRlConfirm] = useState(false);
  const [clearingRl, setClearingRl] = useState(false);

  const loading = cycleLoading || compsLoading || rlLoading;

  // Filtered comps
  const filtered = useMemo(() => {
    let list = comps;
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(
        (c) =>
          c.employeeName?.toLowerCase().includes(s) ||
          c.employeeEmail?.toLowerCase().includes(s) ||
          c.department?.toLowerCase().includes(s),
      );
    }
    if (filterCat) list = list.filter((c) => c.category === filterCat);
    return list;
  }, [comps, search, filterCat]);

  // Stats
  const communicated = comps.filter((c) => c._presented).length;
  const avgChange =
    comps.length > 0
      ? (comps.reduce((s, c) => s + (c.currentBase ? ((c.newBase - c.currentBase) / c.currentBase) * 100 : 0), 0) / comps.length).toFixed(1)
      : 0;
  const categories = [...new Set(comps.map((c) => c.category))];
  const health = useMemo(() => rollupHealth(comps, reportingLines), [comps, reportingLines]);
  const [rebuilding, setRebuilding] = useState(false);

  const handleRebuildRollups = async () => {
    setRebuilding(true);
    try {
      const fixed = await stampSupervisorChains(cycleId);
      toast.success(`Rebuilt manager rollups for ${fixed} compensation${fixed === 1 ? '' : 's'}`);
      refresh();
    } catch {
      toast.error('Failed to rebuild manager rollups');
    }
    setRebuilding(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteCompensation(cycleId, deleteTarget.id);
      await updateCycle(cycleId, { compCount: comps.length - 1 });
      toast.success('Compensation removed');
      refresh();
    } catch {
      toast.error('Failed to delete');
    }
    setDeleting(false);
    setDeleteTarget(null);
  };

  const handleSaveCycleName = async () => {
    if (cycleName.trim() && cycleName.trim() !== cycle.name) {
      await updateCycle(cycleId, { name: cycleName.trim() });
      toast.success('Cycle name updated');
    }
    setEditingName(false);
  };

  const handleClearReportingLines = async () => {
    setClearingRl(true);
    try {
      await deleteAllReportingLines(cycleId);
      toast.success('Reporting structure cleared');
      refresh();
    } catch {
      toast.error('Failed to clear reporting structure');
    }
    setClearingRl(false);
    setClearRlConfirm(false);
  };

  if (!loading && !cycle) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <p className="text-navy-400 mb-4">Cycle not found</p>
        <Button variant="secondary" onClick={() => nav('/admin')}>Back to Dashboard</Button>
      </div>
    );
  }

  const st = cycle ? (CYCLE_STATUSES[cycle.status] || CYCLE_STATUSES.draft) : CYCLE_STATUSES.draft;

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-navy-400">
        <Link to="/admin" className="hover:text-accent-600 transition-colors">Cycles</Link>
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
        <span className="text-navy-700 font-medium">{cycle?.name || '…'}</span>
      </div>

      {/* Cycle header */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div className="flex items-center gap-3">
          {editingName ? (
            <input
              autoFocus
              value={cycleName}
              onChange={(e) => setCycleName(e.target.value)}
              onBlur={handleSaveCycleName}
              onKeyDown={(e) => e.key === 'Enter' && handleSaveCycleName()}
              className="text-display-sm text-navy-900 bg-transparent border-b-2 border-accent-500 focus:outline-none"
            />
          ) : (
            <h1
              className="text-display-sm text-navy-900 cursor-pointer hover:text-accent-700 transition-colors"
              onClick={() => { setCycleName(cycle?.name || ''); setEditingName(true); }}
              title="Click to rename"
            >
              {cycle?.name || '…'}
            </h1>
          )}
          <span className={cn('inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset', st.color, st.ring)}>
            {st.label}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {activeTab === 'employees' ? (
            <>
              <Button variant="secondary" onClick={() => setCsvModalOpen(true)} size="lg">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" /></svg>
                Import CSV
              </Button>
              <Button onClick={() => setEditComp('new')} size="lg">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                Add Compensation
              </Button>
            </>
          ) : (
            <>
              {reportingLines.length > 0 && (
                <Button variant="secondary" onClick={() => setClearRlConfirm(true)} size="lg" className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                  Clear
                </Button>
              )}
              <Button onClick={() => setReportingModalOpen(true)} size="lg">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" /></svg>
                Import Reporting Structure
              </Button>
            </>
          )}
        </div>
      </motion.div>

      {/* Manager rollup health */}
      {!loading && comps.length > 0 && (
        reportingLines.length === 0 ? (
          <Notice tone="warning" title="No reporting structure for this cycle">
            Managers won't see their team in My Team until a reporting structure is imported.
          </Notice>
        ) : health.invalidLines > 0 ? (
          <Notice tone="danger" title={`${health.invalidLines} of ${reportingLines.length} reporting lines don't have valid emails`}>
            Manager rollups can't be built from them. Re-import the reporting structure; files with the name and email columns swapped are now fixed automatically.
          </Notice>
        ) : health.staleChains > 0 ? (
          <Notice
            tone="warning"
            title={`Manager rollups are out of date for ${health.staleChains} compensation${health.staleChains === 1 ? '' : 's'}`}
            action={<Button size="sm" onClick={handleRebuildRollups} loading={rebuilding}>Rebuild rollups</Button>}
          >
            Usually caused by edits or imports made before rollups were rebuilt automatically.
          </Notice>
        ) : health.unmatchedCommunicators > 0 ? (
          <Notice tone="warning" title={`${health.unmatchedCommunicators} of ${health.communicators} communicators aren't in the reporting structure`}>
            Their managers won't see them in My Team. Add them to the reporting structure file and re-import it.
          </Notice>
        ) : (
          <Notice tone="success" title={`Manager rollups are up to date for all ${health.communicators} communicators`} />
        )
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-surface-100 rounded-xl p-1">
        {[
          { key: 'employees', label: 'Employees', count: comps.length, icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" /></svg>
          )},
          { key: 'reporting', label: 'Reporting Structure', count: reportingLines.length, icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" /></svg>
          )},
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all',
              activeTab === tab.key
                ? 'bg-white text-navy-900 shadow-sm'
                : 'text-navy-400 hover:text-navy-600',
            )}
          >
            {tab.icon}
            {tab.label}
            {tab.count > 0 && (
              <span className={cn(
                'ml-1 text-[11px] font-bold px-1.5 py-0.5 rounded-full',
                activeTab === tab.key
                  ? 'bg-accent-100 text-accent-700'
                  : 'bg-surface-200 text-navy-400',
              )}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ═══════ Employees Tab ═══════ */}
      {activeTab === 'employees' && (
        <>
          {/* Quick stats */}
          {!loading && comps.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Employees', value: comps.length },
                { label: 'Communicated', value: `${communicated}/${comps.length} (${Math.round((communicated / comps.length) * 100)}%)` },
                { label: 'Avg Base Change', value: `${avgChange}%` },
                { label: 'Categories', value: categories.length },
              ].map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                  className="bg-white rounded-2xl border border-surface-200/80 p-4 shadow-subtle"
                >
                  <p className="text-xs font-semibold text-navy-400 uppercase tracking-wider">{s.label}</p>
                  <p className="text-xl font-bold text-navy-900 mt-1">{s.value}</p>
                </motion.div>
              ))}
            </div>
          )}

          {/* Filters */}
          <Card className="p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Input
                  placeholder="Search by name, email, or department…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  icon={
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
                  }
                />
              </div>
              <Select
                options={[{ value: '', label: 'All Categories' }, ...CATEGORY_OPTIONS]}
                value={filterCat}
                onChange={(e) => setFilterCat(e.target.value)}
              />
            </div>
          </Card>

          {/* Compensation list */}
          {loading ? (
            <SkeletonRows count={5} />
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-surface-100 flex items-center justify-center text-3xl mb-4">
                {comps.length === 0 ? '📝' : '🔍'}
              </div>
              <h3 className="text-lg font-bold text-navy-900 mb-1">
                {comps.length === 0 ? 'No compensations yet' : 'No results'}
              </h3>
              <p className="text-sm text-navy-400 mb-4">
                {comps.length === 0 ? 'Add your first compensation entry for this cycle.' : 'Try adjusting your search or filter.'}
              </p>
              {comps.length === 0 && <Button onClick={() => setEditComp('new')}>Add Compensation</Button>}
            </div>
          ) : (
            <AnimatedList className="space-y-2">
              {filtered.map((comp) => (
                <Card
                  key={comp.id}
                  hover
                  onClick={() => setEditComp(comp)}
                  className="flex items-center gap-4 p-4 group"
                >
                  <Avatar name={comp.employeeName} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-navy-900 truncate group-hover:text-accent-700 transition-colors">{comp.employeeName || comp.employeeEmail}</p>
                    <p className="text-xs text-navy-400 truncate">{comp.currentTitle}{comp.department ? ` · ${comp.department}` : ''}</p>
                  </div>
                  <div className="hidden lg:block min-w-0 max-w-[160px]">
                    <p className="text-xs text-navy-500 truncate">{comp.communicatorName || comp.communicatorEmail || '—'}</p>
                    {comp.communicatorName && comp.communicatorEmail && (
                      <p className="text-[11px] text-navy-300 truncate">{comp.communicatorEmail}</p>
                    )}
                  </div>
                  <Badge category={comp.category} className="hidden sm:inline-flex" />
                  <div className="text-right hidden md:block">
                    <p className="text-sm font-semibold text-navy-800">{formatCurrency(comp.newBase, comp.currency)}</p>
                    <p className={cn('text-xs font-semibold', comp.newBase >= comp.currentBase ? 'text-emerald-600' : 'text-red-500')}>
                      {pctChange(comp.currentBase, comp.newBase)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => setDeleteTarget(comp)} className="p-1.5 rounded-lg hover:bg-red-50 text-navy-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                    </button>
                  </div>
                </Card>
              ))}
            </AnimatedList>
          )}
        </>
      )}

      {/* ═══════ Reporting Structure Tab ═══════ */}
      {activeTab === 'reporting' && (
        <OrgTreeView reportingLines={reportingLines} compensations={comps} />
      )}

      {/* Compensation Form */}
      <CompensationForm
        open={editComp !== null}
        cycleId={cycleId}
        compensation={editComp === 'new' ? null : editComp}
        totalComps={comps.length}
        onClose={() => { setEditComp(null); refresh(); }}
      />

      {/* CSV Import */}
      <CsvImportModal
        open={csvModalOpen}
        onClose={() => { setCsvModalOpen(false); refresh(); }}
        cycleId={cycleId}
      />

      {/* Reporting Structure Import */}
      <ReportingImportModal
        open={reportingModalOpen}
        onClose={() => { setReportingModalOpen(false); refresh(); }}
        cycleId={cycleId}
        communicatorEmails={comps.map((c) => c.communicatorEmail)}
      />

      {/* Delete Confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Remove Compensation"
        message={`Remove ${deleteTarget?.employeeName || 'this compensation'}? This action cannot be undone.`}
        confirmLabel="Remove"
        danger
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Clear Reporting Lines Confirm */}
      <ConfirmDialog
        open={clearRlConfirm}
        title="Clear Reporting Structure"
        message={`Remove all ${reportingLines.length} reporting lines for this cycle? This action cannot be undone.`}
        confirmLabel="Clear All"
        danger
        loading={clearingRl}
        onConfirm={handleClearReportingLines}
        onCancel={() => setClearRlConfirm(false)}
      />
    </div>
  );
}